// =============================================================
// mox-product-variants.js
// Selector de variantes cross-product para PDP de Shopify.
// Agrupa productos independientes mediante el metacampo
// custom.similar_product (ej. "Cama Sion") y construye
// selectores unificados de opciones (tono, medida, tela, etc.).
// =============================================================

'use strict';

// ─── Configuración ────────────────────────────────────────────
const MOX_PV_CONFIG = {
  CACHE_NS:        'mox_pv_v1_',
  CACHE_TTL:       5 * 60 * 1000, // 5 minutos
  SEARCH_LIMIT:    20,
  DROPDOWN_THRESH: 6,              // valores ≥ N → dropdown
  SWATCH_DIMS:     ['tono', 'color', 'acabado', 'madera', 'finish', 'colour', 'color/tono', 'tonalidad', 'tela', 'medida', 'size', 'talla'],
  IMG_SWATCH_W: 128,
  IMG_SWATCH_H: 160,
};

// =============================================================
// MoxPVAPI — Capa de datos: fetch + caché sessionStorage
// =============================================================
class MoxPVAPI {
  constructor() {
    this._mem = new Map(); // caché en memoria para la sesión actual
  }

  // Devuelve todos los productos del grupo. Primer llamado hace
  // 1 búsqueda + N fetches; llamados subsiguientes usan caché.
  async fetchGroupProducts(groupName) {
    const key = MOX_PV_CONFIG.CACHE_NS + 'group_' + groupName;
    const cached = this._readCache(key);
    if (cached) return cached;

    const handles = await this._searchHandles(groupName);
    if (!handles.length) {
      // No cachear resultado vacío: puede ser error de indexación temporal
      return [];
    }

    const results = await Promise.allSettled(
      handles.map(h => this.fetchProduct(h))
    );

    const products = results
      .filter(r => r.status === 'fulfilled' && r.value)
      .map(r => r.value);

    console.log(`[MoxPV] Grupo "${groupName}" → ${products.length} productos:`, products.map(p => p.handle));

    // Solo cachear si hay más de 1 producto (evita guardar resultado parcial)
    if (products.length > 1) this._writeCache(key, products);
    return products;
  }

  // Busca handles de productos cuyo título contenga groupName.
  // Usa URL string manual: URLSearchParams codifica [ ] como %5B %5D
  // y la Predictive Search API de Shopify no los acepta codificados.
  async _searchHandles(query) {
    const q   = encodeURIComponent(query);
    const lim = MOX_PV_CONFIG.SEARCH_LIMIT;
    const url = `/search/suggest.json?q=${q}&resources[type]=product&resources[limit]=${lim}&resources[options][fields]=title`;

    try {
      const res = await fetch(url);
      if (!res.ok) {
        console.warn(`[MoxPV] Search respondió ${res.status}`);
        return [];
      }
      const json  = await res.json();
      const hits  = json?.resources?.results?.products ?? [];
      const lower = query.toLowerCase();

      console.log(`[MoxPV] Search "${query}" → ${hits.length} hits`, hits.map(p => p.title));

      return hits
        .filter(p => p.title.toLowerCase().includes(lower))
        .map(p => p.handle);
    } catch (e) {
      console.error('[MoxPV] Search error:', e);
      return [];
    }
  }

  // Devuelve el objeto de producto completo desde /products/{handle}.js
  async fetchProduct(handle) {
    const key = MOX_PV_CONFIG.CACHE_NS + 'product_' + handle;

    if (this._mem.has(key)) return this._mem.get(key);
    const cached = this._readCache(key);
    if (cached) {
      this._mem.set(key, cached);
      return cached;
    }

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const product = await res.json();

      this._mem.set(key, product);
      this._writeCache(key, product);
      return product;
    } catch (e) {
      console.error(`[MoxPV] fetchProduct error (${handle}):`, e);
      return null;
    }
  }

  _readCache(key) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const { data, ts } = JSON.parse(raw);
      if (Date.now() - ts > MOX_PV_CONFIG.CACHE_TTL) {
        sessionStorage.removeItem(key);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  }

  _writeCache(key, data) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
    } catch {
      // sessionStorage llena — ignorar, sigue funcionando sin caché
    }
  }
}

// =============================================================
// MoxPVMatrix — Lógica de combinaciones y disponibilidad
// =============================================================
class MoxPVMatrix {
  constructor() {
    this.dimensions = []; // [{ name: string, values: string[] }]
    this.entries     = new Map(); // "v1|v2|v3" → { product, variant, available, url }
  }

  // Construye el mapa de combinaciones desde el array de productos
  build(products, groupName) {
    this.dimensions = [];
    this.entries.clear();

    if (!products.length) return;

    // Detecta si los productos tienen opciones significativas.
    // /products/{handle}.js devuelve options como objetos {name, values, ...}
    // pero el JSON embebido desde Liquid puede traerlos como strings.
    const optName = o => (typeof o === 'object' && o !== null) ? o.name : o;

    const hasRealOptions = products.some(p =>
      Array.isArray(p.options) &&
      p.options.length > 0 &&
      !(p.options.length === 1 && optName(p.options[0]) === 'Title')
    );

    if (hasRealOptions) {
      this._buildFromOptions(products, optName);
    } else {
      this._buildFromTitles(products, groupName);
    }
  }

  // ── Estrategia A: opciones nativas de Shopify ───────────────
  _buildFromOptions(products, optName) {
    // optName: función normalizadora (objeto o string) → string
    optName = optName ?? (o => (typeof o === 'object' && o !== null) ? o.name : o);
    const dimMap = new Map(); // optionName → Set<string>

    for (const product of products) {
      if (!Array.isArray(product.options)) continue;
      product.options.forEach((opt, idx) => {
        const name = optName(opt);
        if (!name || name === 'Title') return;
        if (!dimMap.has(name)) dimMap.set(name, new Set());

        for (const v of product.variants) {
          const val = v[`option${idx + 1}`];
          if (val) dimMap.get(name).add(val);
        }
      });
    }

    this.dimensions = Array.from(dimMap.entries()).map(([name, vals]) => ({
      name,
      values: Array.from(vals),
    }));

    for (const product of products) {
      if (!Array.isArray(product.options)) continue;
      const optNames = product.options.map(optName).filter(n => n && n !== 'Title');

      for (const variant of product.variants) {
        const parts = optNames.map((_, i) => variant[`option${i + 1}`]).filter(Boolean);
        if (parts.length !== optNames.length) continue;

        const key = parts.join('|');
        this.entries.set(key, {
          product,
          variant,
          available: Boolean(variant.available),
          url:       `/products/${product.handle}?variant=${variant.id}`,
        });
      }
    }
  }

  // ── Estrategia B: parsing por título (fallback) ─────────────
  _buildFromTitles(products, groupName) {
    const re      = new RegExp(groupName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    const wordSets = [];

    for (const product of products) {
      const words = product.title
        .replace(re, '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      words.forEach((w, i) => {
        if (!wordSets[i]) wordSets[i] = new Set();
        wordSets[i].add(w);
      });
    }

    this.dimensions = wordSets.map((vals, i) => ({
      name:   `Opción ${i + 1}`,
      values: Array.from(vals),
    }));

    for (const product of products) {
      const words = product.title
        .replace(re, '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      const key     = words.join('|');
      const variant = product.variants[0] ?? null;

      this.entries.set(key, {
        product,
        variant,
        available: Boolean(product.available),
        url:       `/products/${product.handle}`,
      });
    }
  }

  // ── Consultas ───────────────────────────────────────────────

  // Devuelve la entrada para una selección completa, o null
  getEntry(selection) {
    const key = this.dimensions.map(d => selection[d.name] ?? '').join('|');
    return this.entries.get(key) ?? null;
  }

  // Todas las entradas que contienen un valor en una dimensión dada
  getEntriesWithValue(dimName, value) {
    const idx = this.dimensions.findIndex(d => d.name === dimName);
    if (idx < 0) return [];

    const out = [];
    for (const [key, entry] of this.entries) {
      if (key.split('|')[idx] === value) out.push(entry);
    }
    return out;
  }

  // Un valor está disponible si existe al menos una entrada que lo incluya
  // y sea compatible con la selección actual en las otras dimensiones
  isValueAvailable(dimName, value, currentSelection) {
    const idx = this.dimensions.findIndex(d => d.name === dimName);
    if (idx < 0) return false;

    for (const [key] of this.entries) {
      const parts = key.split('|');
      if (parts[idx] !== value) continue;

      const compatible = this.dimensions.every((d, i) => {
        if (d.name === dimName) return true;
        if (!currentSelection[d.name]) return true;
        return parts[i] === currentSelection[d.name];
      });

      if (compatible) return true;
    }

    return false;
  }

  // Cuando se selecciona un nuevo valor, calcula la selección más cercana válida
  resolveSelection(dimName, newValue, currentSelection) {
    const candidate = { ...currentSelection, [dimName]: newValue };
    if (this.getEntry(candidate)) return candidate;

    // Buscar entrada que tenga el nuevo valor y preferir la disponible
    const entries = this.getEntriesWithValue(dimName, newValue);
    if (!entries.length) return candidate;

    const best = entries.find(e => e.available) ?? entries[0];
    return this._selectionFromKey(this._keyForEntry(best));
  }

  // Determina la selección activa a partir del handle y variantId actuales
  getCurrentSelection(currentHandle, variantId) {
    for (const [key, entry] of this.entries) {
      if (entry.product.handle !== currentHandle) continue;
      if (variantId && entry.variant?.id !== variantId) continue;
      return this._selectionFromKey(key);
    }
    return {};
  }

  _selectionFromKey(key) {
    const parts = key.split('|');
    const sel   = {};
    this.dimensions.forEach((d, i) => { sel[d.name] = parts[i] ?? ''; });
    return sel;
  }

  _keyForEntry(target) {
    for (const [key, e] of this.entries) {
      if (e === target) return key;
    }
    return '';
  }
}

// =============================================================
// MoxPVUI — Capa de renderizado DOM
// =============================================================
class MoxPVUI {
  constructor(container) {
    this.container   = container;
    this.loadingEl   = container.querySelector('.mox-pv__loading');
    this.selectorsEl = container.querySelector('.mox-pv__selectors');
  }

  showLoading() {
    this.loadingEl  && (this.loadingEl.hidden  = false);
    this.selectorsEl && (this.selectorsEl.hidden = true);
  }

  showSelectors() {
    this.loadingEl  && (this.loadingEl.hidden  = true);
    this.selectorsEl && (this.selectorsEl.hidden = false);
  }

  hide() {
    this.loadingEl  && (this.loadingEl.hidden  = true);
    this.selectorsEl && (this.selectorsEl.hidden = true);
  }

  // Renderizado completo inicial
  render(dimensions, selection, matrix, products) {
    this.selectorsEl.innerHTML = '';
    for (const dim of dimensions) {
      this.selectorsEl.appendChild(
        this._buildGroup(dim, selection, matrix, products)
      );
    }
    this.showSelectors();
  }

  // Actualiza imágenes de swatches cuando cambia la selección
  _updateSwatchImages(selection, matrix) {
    for (const el of this.container.querySelectorAll('[data-mpv-swatch]')) {
      const { mpvDim: dimName, mpvValue: value } = el.dataset;
      const dimIdx = matrix.dimensions.findIndex(d => d.name === dimName);
      let bestEntry = null;

      for (const [key, entry] of matrix.entries) {
        const parts = key.split('|');
        if (parts[dimIdx] !== value) continue;
        const fits = matrix.dimensions.every((d, i) => {
          if (d.name === dimName) return true;
          if (!selection[d.name])  return true;
          return parts[i] === selection[d.name];
        });
        if (fits) { bestEntry = entry; break; }
        if (!bestEntry) bestEntry = entry;
      }

      if (!bestEntry) continue;
      const raw = bestEntry.variant?.featured_image?.src || bestEntry.product.featured_image || '';
      const img = el.querySelector('img');
      if (img && raw) img.src = this._cdnSize(raw, MOX_PV_CONFIG.IMG_SWATCH_W, MOX_PV_CONFIG.IMG_SWATCH_H);
    }
  }

  // Actualiza solo los estados activo/deshabilitado sin re-renderizar el DOM
  updateActiveStates(selection, matrix) {
    this._updateSwatchImages(selection, matrix);

    // Pills
    for (const el of this.container.querySelectorAll('[data-mpv-pill]')) {
      const { mpvDim: dim, mpvValue: val } = el.dataset;
      const active    = selection[dim] === val;
      const available = matrix.isValueAvailable(dim, val, selection);
      el.classList.toggle('is-active',   active);
      el.classList.toggle('is-disabled', !available && !active);
      el.setAttribute('aria-pressed', String(active));
      el.disabled = !available && !active;
    }

    // Swatches
    for (const el of this.container.querySelectorAll('[data-mpv-swatch]')) {
      const { mpvDim: dim, mpvValue: val } = el.dataset;
      const active    = selection[dim] === val;
      const available = matrix.isValueAvailable(dim, val, selection);
      el.classList.toggle('is-active',   active);
      el.classList.toggle('is-disabled', !available && !active);
      el.setAttribute('aria-pressed', String(active));
    }

    // Selects
    for (const el of this.container.querySelectorAll('[data-mpv-select]')) {
      const dim = el.dataset.mpvDim;
      if (selection[dim]) el.value = selection[dim];
      for (const opt of el.options) {
        if (!opt.value) continue;
        opt.disabled = !matrix.isValueAvailable(dim, opt.value, selection);
      }
    }

    // Etiquetas de valor seleccionado
    for (const el of this.container.querySelectorAll('[data-mpv-val-label]')) {
      const dim = el.dataset.mpvValLabel;
      el.textContent = selection[dim] ? `: ${selection[dim]}` : '';
    }
  }

  // ── Constructores de elementos ──────────────────────────────

  _buildGroup(dim, selection, matrix, products) {
    const group = document.createElement('div');
    group.className  = 'mox-pv__group';
    group.dataset.dim = dim.name;

    // Label
    const label = document.createElement('div');
    label.className = 'mox-pv__label hdt-product-form__label hdt-s-text';

    const nameSpan = document.createElement('span');
    nameSpan.textContent = dim.name;

    const valSpan = document.createElement('span');
    valSpan.className = 'mox-pv__selected-value';
    valSpan.dataset.mpvValLabel = dim.name;
    valSpan.textContent = selection[dim.name] ? `: ${selection[dim.name]}` : '';

    nameSpan.appendChild(valSpan);
    label.appendChild(nameSpan);

    // Options container
    const opts = document.createElement('div');
    const isSwatch   = this._isSwatchDim(dim.name);
    const isDropdown = !isSwatch && dim.values.length >= MOX_PV_CONFIG.DROPDOWN_THRESH;

    if (isSwatch) {
      opts.className = 'mox-pv__options mox-pv__options--swatches';
      for (const val of dim.values) {
        opts.appendChild(this._buildSwatch(dim.name, val, selection, matrix));
      }
    } else if (isDropdown) {
      opts.className = 'mox-pv__options mox-pv__options--dropdown';
      opts.appendChild(this._buildDropdown(dim.name, dim.values, selection, matrix));
    } else {
      opts.className = 'mox-pv__options';
      for (const val of dim.values) {
        opts.appendChild(this._buildPill(dim.name, val, selection, matrix));
      }
    }

    group.appendChild(label);
    group.appendChild(opts);
    return group;
  }

  _buildPill(dimName, value, selection, matrix) {
    const active    = selection[dimName] === value;
    const available = matrix.isValueAvailable(dimName, value, selection);

    const btn = document.createElement('button');
    btn.type      = 'button';
    btn.className = `mox-pv__pill${active ? ' is-active' : ''}${!available && !active ? ' is-disabled' : ''}`;
    btn.textContent = value;
    btn.disabled    = !available && !active;
    btn.dataset.mpvPill  = '';
    btn.dataset.mpvDim   = dimName;
    btn.dataset.mpvValue = value;
    btn.setAttribute('aria-pressed', String(active));

    return btn;
  }

  _buildSwatch(dimName, value, selection, matrix) {
    const active    = selection[dimName] === value;
    const available = matrix.isValueAvailable(dimName, value, selection);

    // Busca la imagen del producto correspondiente
    let imgSrc = '';
    const dimIdx = matrix.dimensions.findIndex(d => d.name === dimName);
    let bestEntry = null;

    for (const [key, entry] of matrix.entries) {
      const parts = key.split('|');
      if (parts[dimIdx] !== value) continue;
      // Preferir la entrada que coincida con la selección actual en las demás dimensiones
      const fits = matrix.dimensions.every((d, i) => {
        if (d.name === dimName) return true;
        if (!selection[d.name])  return true;
        return parts[i] === selection[d.name];
      });
      if (fits) { bestEntry = entry; break; }
      if (!bestEntry) bestEntry = entry;
    }

    if (bestEntry) {
      const raw = bestEntry.variant?.featured_image?.src || bestEntry.product.featured_image || '';
      imgSrc = raw ? this._cdnSize(raw, MOX_PV_CONFIG.IMG_SWATCH_W, MOX_PV_CONFIG.IMG_SWATCH_H) : '';
    }

    const btn = document.createElement('button');
    btn.type      = 'button';
    btn.className = `mox-pv__swatch${active ? ' is-active' : ''}${!available && !active ? ' is-disabled' : ''}`;
    btn.title     = value;
    btn.dataset.mpvSwatch = '';
    btn.dataset.mpvDim    = dimName;
    btn.dataset.mpvValue  = value;
    btn.setAttribute('aria-pressed', String(active));
    btn.setAttribute('aria-label',   value);

    const imgWrap = document.createElement('span');
    imgWrap.className = 'mox-pv__swatch-img';

    if (imgSrc) {
      const img  = document.createElement('img');
      img.src    = imgSrc;
      img.alt    = value;
      img.width  = MOX_PV_CONFIG.IMG_SWATCH_W;
      img.height = MOX_PV_CONFIG.IMG_SWATCH_H;
      img.loading = 'lazy';
      imgWrap.appendChild(img);
    }

    const lbl = document.createElement('span');
    lbl.className = 'mox-pv__swatch-label';
    lbl.textContent = value;
    lbl.setAttribute('aria-hidden', 'true');

    btn.appendChild(imgWrap);
    btn.appendChild(lbl);
    return btn;
  }

  _buildDropdown(dimName, values, selection, matrix) {
    const wrap = document.createElement('div');
    wrap.className = 'mox-pv__select-wrapper';

    const sel = document.createElement('select');
    sel.className = 'mox-pv__select hdt-select';
    sel.dataset.mpvSelect = '';
    sel.dataset.mpvDim    = dimName;
    sel.setAttribute('aria-label', dimName);

    const placeholder = document.createElement('option');
    placeholder.value    = '';
    placeholder.textContent = `Seleccionar ${dimName}`;
    placeholder.disabled = true;
    placeholder.hidden   = Boolean(selection[dimName]);
    sel.appendChild(placeholder);

    for (const val of values) {
      const opt  = document.createElement('option');
      opt.value   = val;
      opt.textContent = val;
      opt.selected = selection[dimName] === val;
      opt.disabled = !matrix.isValueAvailable(dimName, val, selection);
      sel.appendChild(opt);
    }

    const caret = document.createElement('span');
    caret.className = 'mox-pv__select-caret';
    caret.setAttribute('aria-hidden', 'true');
    caret.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    wrap.appendChild(sel);
    wrap.appendChild(caret);
    return wrap;
  }

  // ── Helpers ─────────────────────────────────────────────────

  _isSwatchDim(name) {
    return MOX_PV_CONFIG.SWATCH_DIMS.includes(name.toLowerCase());
  }

  // Aplica parámetros de tamaño al CDN de Shopify
  _cdnSize(src, w, h) {
    if (!src) return '';
    try {
      const url = new URL(src.startsWith('//') ? `https:${src}` : src);
      url.searchParams.set('width',  String(w));
      if (h) url.searchParams.set('height', String(h));
      url.searchParams.set('crop',   'top');
      return url.toString();
    } catch {
      return src;
    }
  }
}

// =============================================================
// MoxPVBuyButton — Integración con el botón de compra
// =============================================================
class MoxPVBuyButton {
  constructor() {
    this._btn  = null;
    this._msg  = null;

    document.addEventListener('mox-pv:selection', e => this._onSelection(e.detail));
  }

  attach(formId) {
    const form = document.getElementById(formId);
    if (!form) return;

    this._btn = form.querySelector('[type="submit"], [name="add"]');
    if (!this._btn) return;

    this._msg = document.createElement('p');
    this._msg.className = 'mox-pv__btn-msg';
    this._msg.setAttribute('aria-live', 'polite');
    this._msg.hidden = true;
    this._btn.closest('.hdt-product-form__submit, .product-form__buttons')
      ?.appendChild(this._msg)
      ?? this._btn.parentElement.insertAdjacentElement('afterend', this._msg);
  }

  _onSelection({ allSelected, valid, available }) {
    if (!this._btn) return;

    if (!allSelected) {
      this._disable('Selecciona todas las opciones para continuar.');
    } else if (!valid) {
      this._disable('Esta combinación no está disponible.');
    } else if (!available) {
      this._disable('Esta configuración está agotada.');
    } else {
      this._enable();
    }
  }

  _disable(msg) {
    if (this._btn) this._btn.disabled = true;
    if (this._msg) { this._msg.textContent = msg; this._msg.hidden = false; }
  }

  _enable() {
    if (this._btn) this._btn.disabled = false;
    if (this._msg) this._msg.hidden = true;
  }
}

// =============================================================
// MoxProductVariants — Orquestador principal
// =============================================================
class MoxProductVariants {
  constructor(container) {
    this.container     = container;
    this.group         = container.dataset.group;
    this.currentHandle = container.dataset.currentHandle;
    this.formId        = container.dataset.formId;

    const dataEl    = document.getElementById(container.dataset.dataId);
    this.seedData   = dataEl ? JSON.parse(dataEl.textContent) : null;

    this.api       = new MoxPVAPI();
    this.matrix    = new MoxPVMatrix();
    this.ui        = new MoxPVUI(container);
    this.buyBtn    = new MoxPVBuyButton();
    this.selection = {};
  }

  async init() {
    if (!this.group) { this.ui.hide(); return; }

    this.ui.showLoading();
    this.buyBtn.attach(this.formId);

    try {
      // 1. Obtener todos los productos del grupo
      let products = await this.api.fetchGroupProducts(this.group);

      // Asegurar que el producto actual esté incluido
      if (!products.some(p => p.handle === this.currentHandle)) {
        const current = await this.api.fetchProduct(this.currentHandle);
        if (current) products = [current, ...products];
      }

      // Filtrar productos despublicados o sin datos
      products = products.filter(p => p && p.handle && p.variants?.length);

      if (!products.length) { this.ui.hide(); return; }

      // 2. Construir matrix de combinaciones
      this.matrix.build(products, this.group);

      if (!this.matrix.dimensions.length) { this.ui.hide(); return; }

      // 3. Selección inicial desde URL
      const params    = new URLSearchParams(window.location.search);
      const variantId = params.get('variant') ? parseInt(params.get('variant'), 10) : null;
      this.selection  = this.matrix.getCurrentSelection(this.currentHandle, variantId);

      // 4. Renderizar
      this.ui.render(this.matrix.dimensions, this.selection, this.matrix, products);

      // 5. Ocultar picker nativo de Ecomus
      // data-type en Ecomus con bloques @theme es "@theme", no "_product-variant-picker"
      // Usamos la clase CSS del wrapper que siempre está presente
      const pickerEl = this.container.closest('.hdt-product__variant-picker');
      if (pickerEl) pickerEl.setAttribute('data-mox-pv-active', '');

      // 6. Registrar eventos
      this._bindEvents();

      // 7. Notificar estado inicial al botón de compra
      this._dispatch();

    } catch (err) {
      console.error('[MoxPV] init error:', err);
      this.ui.hide();
    }
  }

  _bindEvents() {
    this.container.addEventListener('click', e => {
      const pill   = e.target.closest('[data-mpv-pill]');
      const swatch = e.target.closest('[data-mpv-swatch]');
      if (pill   && !pill.disabled)                         this._onOptionChange(pill.dataset.mpvDim,   pill.dataset.mpvValue);
      if (swatch && !swatch.classList.contains('is-disabled')) this._onOptionChange(swatch.dataset.mpvDim, swatch.dataset.mpvValue);
    });

    this.container.addEventListener('change', e => {
      const sel = e.target.closest('[data-mpv-select]');
      if (sel?.value) this._onOptionChange(sel.dataset.mpvDim, sel.value);
    });
  }

  _onOptionChange(dimName, value) {
    const newSel = this.matrix.resolveSelection(dimName, value, this.selection);
    const entry  = this.matrix.getEntry(newSel);

    if (entry) {
      MoxPVNavigation.go(entry);
      return;
    }

    // Combinación inválida → solo actualizar UI
    this.selection = newSel;
    this.ui.updateActiveStates(this.selection, this.matrix);
    this._dispatch();
  }

  _dispatch() {
    const entry       = this.matrix.getEntry(this.selection);
    const allSelected = this.matrix.dimensions.every(d => Boolean(this.selection[d.name]));

    document.dispatchEvent(new CustomEvent('mox-pv:selection', {
      detail: {
        selection:   this.selection,
        entry,
        allSelected,
        valid:       Boolean(entry),
        available:   entry?.available ?? false,
      }
    }));
  }
}

// =============================================================
// MoxPVNavigation — Transición sin recarga usando Section Rendering API
// =============================================================
const MoxPVNavigation = {

  // En templates JSON de Shopify OS 2.0 el ID real es:
  // "shopify-section-template--XXXXXX__main-product"  (doble guion bajo antes de la key)
  // Por eso buscamos por sufijo con __, no por ID exacto.
  _findSection() {
    return (
      document.querySelector('[id$="__main-product"]') ||
      document.getElementById('shopify-section-main-product')
    );
  },

  async go(entry) {
    const sectionEl = this._findSection();
    if (!sectionEl) {
      console.error('[MoxPV] No se encontró #shopify-section-*--main-product en el DOM');
      window.location.href = entry.url;
      return;
    }

    // ID real del section (con prefijo de template si aplica)
    const sectionId = sectionEl.id.replace('shopify-section-', '');

    this._overlay(sectionEl, true);

    try {
      const sep      = entry.url.includes('?') ? '&' : '?';
      const fetchUrl = `${entry.url}${sep}sections=${encodeURIComponent(sectionId)}`;
      const res      = await fetch(fetchUrl);

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const json = await res.json();
      const html = json[sectionId];

      if (!html) {
        // Log para diagnóstico: muestra qué keys devolvió Shopify
        console.error('[MoxPV] La key esperada no está en la respuesta.', {
          expected: sectionId,
          received: Object.keys(json),
        });
        throw new Error('empty section');
      }

      // Parsear y reemplazar contenido
      const doc        = new DOMParser().parseFromString(html, 'text/html');
      const newSection = doc.body.firstElementChild;
      if (!newSection) throw new Error('no element in parsed html');

      sectionEl.innerHTML = newSection.innerHTML;
      sectionEl.className = newSection.className;

      // Re-ejecutar scripts inline para que web components de Ecomus se reinicialicen
      sectionEl.querySelectorAll('script').forEach(old => {
        const neo = document.createElement('script');
        for (const a of old.attributes) neo.setAttribute(a.name, a.value);
        neo.textContent = old.textContent;
        old.replaceWith(neo);
      });

      // Actualizar URL e historial solo tras éxito
      history.pushState({ moxPvUrl: entry.url }, '', entry.url);

      // Actualizar título
      const newTitle = doc.querySelector('title');
      if (newTitle) document.title = newTitle.textContent;

      // Re-inicializar MoxPV en el nuevo HTML
      initMoxPV();

    } catch (e) {
      console.error('[MoxPV] Section render error:', e);
      window.location.href = entry.url;
    } finally {
      this._overlay(sectionEl, false);
    }
  },

  _overlay(sectionEl, show) {
    let el = document.getElementById('mox-pv-overlay');
    if (show) {
      if (el) return;
      el = document.createElement('div');
      el.id = 'mox-pv-overlay';
      el.setAttribute('aria-hidden', 'true');
      sectionEl.style.position = 'relative';
      sectionEl.appendChild(el);
    } else {
      el?.remove();
      if (sectionEl) sectionEl.style.position = '';
    }
  },
};

// Botón atrás/adelante del browser
window.addEventListener('popstate', e => {
  if (e.state?.moxPvUrl) {
    MoxPVNavigation.go({ url: e.state.moxPvUrl, available: true, product: {}, variant: {} });
  }
});

// =============================================================
// Bootstrap — inicializa todas las instancias en la página
// =============================================================
function initMoxPV() {
  document.querySelectorAll('.mox-pv[data-group]').forEach(container => {
    new MoxProductVariants(container).init();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMoxPV);
} else {
  typeof requestIdleCallback !== 'undefined'
    ? requestIdleCallback(initMoxPV, { timeout: 2000 })
    : setTimeout(initMoxPV, 0);
}
